import Phaser from 'phaser';
import { Direction } from '@shared/types/entity';
import { GAME_FONT_FAMILY, TILE_SIZE } from '@shared/constants';
import {
  getBodyCenterOffset,
  BODY_COLUMNS,
  WALK_ANIM_FRAME_RATE,
} from '../../sprites/BodySpriteManager';
import { getHeadFrameForDirection } from '../../sprites/HeadSpriteManager';
import { getWeaponSheetOffsetForTextureKey } from '../../sprites/WeaponSpriteManager';
import { getHelmetFrameForDirection } from '../../sprites/HelmetSpriteManager';
import { getBootFrameForDirection } from '../../sprites/BootSpriteManager';

/**
 * Panel de DEBUG de frames del personaje local.
 *
 * Muestra el personaje con su equipamiento ACTUAL (body + head + helm + weapon +
 * shield + boots, leídos de los sprites reales del render) en grande, y permite
 * navegar cuadro por cuadro con flechas ◀ ▶ para encontrar en qué frame se
 * desincronizan las partes:
 *
 * - Modo "TODAS": las flechas mueven la columna BASE (body/arma/escudo juntos,
 *   replica el frame-lock in-game).
 * - Modo por parte: seleccionás BODY/ARMA/ESCUDO y la flecha aplica un SHIFT
 *   persistente a esa parte (corre su colección de frames ±N). El shift se
 *   mantiene durante el playback — sirve para probar si correr los tiles de
 *   una anim arregla una desincronización. Se puede ajustar en vivo mientras
 *   camina.
 * - Play/Pause: reproduce el walk al mismo tempo que in-game (canónico de AO,
 *   ~18fps) avanzando la columna base manualmente; cada parte muestra
 *   base + shift.
 *
 * Head/helm/boots no tienen frames de walk (1 frame por dirección): quedan
 * fijos según la dirección elegida.
 *
 * La composición usa un Container escalado con los offsets NATIVOS de GameScene
 * (WEAPON_OFFSET_X/Y, HEAD_OFFSET_FROM_BODY_BOTTOM, etc.), así lo que se ve acá
 * replica 1:1 el layout in-game.
 */

function withGameFont<T extends Phaser.Types.GameObjects.Text.TextStyle>(style?: T): T {
  return {
    fontFamily: GAME_FONT_FAMILY,
    resolution: 2,
    ...style,
  } as T;
}

/** Texture keys actuales de cada capa del player local (null = capa ausente). */
export type LocalPlayerSpriteKeys = {
  bodyId: number;
  bodyKey: string | null;
  headKey: string | null;
  weaponKey: string | null;
  shieldKey: string | null;
  helmKey: string | null;
  bootKey: string | null;
};

type CharFrameDebugPanelCallbacks = {
  onMove(x: number, y: number): void;
  getLocalPlayerSpriteKeys(): LocalPlayerSpriteKeys;
};

type CharFrameDebugPanelOptions = {
  initialX: number;
  initialY: number;
  HEAD_OFFSET_FROM_BODY_BOTTOM: number;
  HEAD_FRAME_HEIGHT: number;
  WEAPON_OFFSET_X: number;
  WEAPON_OFFSET_Y: number;
  WEAPON_LATERAL_DX: number;
  SHIELD_OFFSET_X: number;
};

type SteppablePart = 'body' | 'weapon' | 'shield';
type PartSelection = 'all' | SteppablePart;

type PlacedObject =
  | Phaser.GameObjects.Rectangle
  | Phaser.GameObjects.Text
  | Phaser.GameObjects.Container;

const DIR_ROW: Record<Direction, number> = {
  [Direction.DOWN]: 0,
  [Direction.UP]: 1,
  [Direction.LEFT]: 2,
  [Direction.RIGHT]: 3,
};

const DIR_ORDER: Direction[] = [Direction.DOWN, Direction.UP, Direction.LEFT, Direction.RIGHT];

const DIR_LABEL: Record<Direction, string> = {
  [Direction.DOWN]: 'ABAJO',
  [Direction.UP]: 'ARRIBA',
  [Direction.LEFT]: 'IZQ',
  [Direction.RIGHT]: 'DER',
};

const PART_ORDER: PartSelection[] = ['all', 'body', 'weapon', 'shield'];

const PART_LABEL: Record<PartSelection, string> = {
  all: 'TODAS',
  body: 'BODY',
  weapon: 'ARMA',
  shield: 'ESCUDO',
};

function gridFrameName(row: number, col: number): string {
  return `r${row}c${col}`;
}

// Layout del panel (offsets relativos al centro)
const PANEL_W = 480;
const PANEL_H = 520;
const PREVIEW_BOX = { dx: 0, dy: -11, w: 360, h: 322 };
const FEET_DY = 110; // pies del personaje (y local del container)
const ZOOM_MIN = 2;
const ZOOM_MAX = 6;
const ZOOM_DEFAULT = 4;
// Mismo tempo que el walk in-game (canónico de AO: ~55.4ms/frame)
const PLAY_FPS = WALK_ANIM_FRAME_RATE;
const DEPTH_BG = 1100;
const DEPTH_UI = 1101;
const DEPTH_LINES = 1102;

export class CharFrameDebugPanel {
  private readonly scene: Phaser.Scene;
  private readonly callbacks: CharFrameDebugPanelCallbacks;
  private readonly opts: CharFrameDebugPanelOptions;

  private panelX: number;
  private panelY: number;
  private isOpen = false;
  private isDragging = false;
  private dragOffsetX = 0;
  private dragOffsetY = 0;

  // Elementos con offset fijo respecto al centro del panel (para move()).
  private readonly placed: Array<{ obj: PlacedObject; dx: number; dy: number }> = [];

  private title!: Phaser.GameObjects.Text;
  private dirButtons = new Map<Direction, Phaser.GameObjects.Text>();
  private partButtons = new Map<PartSelection, Phaser.GameObjects.Text>();
  private playButton!: Phaser.GameObjects.Text;
  private zoomLabel!: Phaser.GameObjects.Text;
  private statusLabel!: Phaser.GameObjects.Text;
  private keysLabel!: Phaser.GameObjects.Text;
  private previewContainer!: Phaser.GameObjects.Container;
  private hLine!: Phaser.GameObjects.Rectangle;
  private tileBox!: Phaser.GameObjects.Rectangle;

  private sprites!: {
    body: Phaser.GameObjects.Sprite;
    head: Phaser.GameObjects.Sprite;
    helm: Phaser.GameObjects.Sprite;
    weapon: Phaser.GameObjects.Sprite;
    shield: Phaser.GameObjects.Sprite;
    boots: Phaser.GameObjects.Sprite;
  };

  // Estado del stepper
  private direction: Direction = Direction.DOWN;
  private selectedPart: PartSelection = 'all';
  // Columna base (la que avanza el playback / las flechas en modo TODAS) +
  // shift persistente por parte (flechas en modo por-parte). La columna
  // mostrada de cada parte es base + shift, con wrap.
  private baseCol = 0;
  private shifts: Record<SteppablePart, number> = { body: 0, weapon: 0, shield: 0 };
  private zoom = ZOOM_DEFAULT;
  private playing = false;
  private playAccumulatorMs = 0;
  private lastKeys: LocalPlayerSpriteKeys | null = null;

  constructor(
    scene: Phaser.Scene,
    callbacks: CharFrameDebugPanelCallbacks,
    options: CharFrameDebugPanelOptions
  ) {
    this.scene = scene;
    this.callbacks = callbacks;
    this.opts = options;
    this.panelX = options.initialX;
    this.panelY = options.initialY;
    this.buildPanel();
    this.setVisible(false);
    this.scene.events.on(Phaser.Scenes.Events.UPDATE, this.onSceneUpdate, this);
  }

  // ---------------------------------------------------------------------
  // API pública (mismo contrato que las galerías)
  // ---------------------------------------------------------------------

  toggle(): void {
    this.isOpen = !this.isOpen;
    this.setVisible(this.isOpen);
  }

  open(): void {
    this.isOpen = true;
    this.setVisible(true);
  }

  close(): void {
    this.isOpen = false;
    this.setVisible(false);
  }

  isOpenPanel(): boolean {
    return this.isOpen;
  }

  handlePointerMove(pointer: Phaser.Input.Pointer): void {
    if (!this.isDragging) return;
    const newX = pointer.x - this.dragOffsetX;
    const newY = pointer.y - this.dragOffsetY;
    this.move(newX, newY);
    this.callbacks.onMove(newX, newY);
  }

  handlePointerUp(pointer: Phaser.Input.Pointer): void {
    if (this.isDragging) {
      this.isDragging = false;
      pointer.event.stopPropagation();
    }
  }

  isDraggingPanel(): boolean {
    return this.isDragging;
  }

  isPointerOver(pointer: Phaser.Input.Pointer): boolean {
    if (!this.isOpen) return false;
    const left = this.panelX - PANEL_W / 2;
    const right = this.panelX + PANEL_W / 2;
    const top = this.panelY - PANEL_H / 2;
    const bottom = this.panelY + PANEL_H / 2;
    return pointer.x >= left && pointer.x <= right && pointer.y >= top && pointer.y <= bottom;
  }

  // ---------------------------------------------------------------------
  // Construcción
  // ---------------------------------------------------------------------

  private place<T extends PlacedObject>(obj: T, dx: number, dy: number): T {
    obj.setPosition(this.panelX + dx, this.panelY + dy);
    this.placed.push({ obj, dx, dy });
    return obj;
  }

  private makeButton(
    label: string,
    dx: number,
    dy: number,
    fontSize: string,
    onClick: () => void
  ): Phaser.GameObjects.Text {
    const txt = this.scene.add
      .text(0, 0, label, withGameFont({
        fontSize,
        color: '#ffffff',
        backgroundColor: '#444444',
      }))
      .setOrigin(0.5, 0.5)
      .setPadding(6, 3, 6, 3)
      .setDepth(DEPTH_UI)
      .setScrollFactor(0)
      .setVisible(false)
      .setInteractive({ useHandCursor: true });
    txt.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      onClick();
      pointer.event.stopPropagation();
    });
    return this.place(txt, dx, dy);
  }

  private buildPanel(): void {
    this.place(
      this.scene.add
        .rectangle(0, 0, PANEL_W, PANEL_H, 0x111111, 0.92)
        .setOrigin(0.5)
        .setStrokeStyle(1, 0x666666)
        .setDepth(DEPTH_BG)
        .setScrollFactor(0)
        .setVisible(false),
      0,
      0
    );

    this.title = this.place(
      this.scene.add
        .text(0, 0, 'Debug de frames del personaje', withGameFont({
          fontSize: '16px',
          color: '#ffffff',
        }))
        .setOrigin(0.5, 0.5)
        .setDepth(DEPTH_UI)
        .setScrollFactor(0)
        .setVisible(false)
        .setInteractive({ useHandCursor: true }),
      0,
      -PANEL_H / 2 + 16
    );

    this.title.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      this.isDragging = true;
      this.dragOffsetX = pointer.x - this.panelX;
      this.dragOffsetY = pointer.y - this.panelY;
      pointer.event.stopPropagation();
    });

    const closeButton = this.scene.add
      .text(0, 0, '✕', withGameFont({
        fontSize: '14px',
        color: '#ffffff',
        backgroundColor: '#555555',
      }))
      .setOrigin(1, 0)
      .setPadding(4, 2, 4, 2)
      .setDepth(DEPTH_UI)
      .setScrollFactor(0)
      .setVisible(false)
      .setInteractive({ useHandCursor: true });
    closeButton.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      this.close();
      pointer.event.stopPropagation();
    });
    this.place(closeButton, PANEL_W / 2 - 8, -PANEL_H / 2 + 8);

    // Fila de direcciones
    const dirXs = [-150, -50, 50, 150];
    DIR_ORDER.forEach((dir, i) => {
      const btn = this.makeButton(DIR_LABEL[dir], dirXs[i], -PANEL_H / 2 + 44, '12px', () => {
        this.setDirection(dir);
      });
      this.dirButtons.set(dir, btn);
    });

    // Fila de selección de parte
    PART_ORDER.forEach((part, i) => {
      const btn = this.makeButton(PART_LABEL[part], dirXs[i], -PANEL_H / 2 + 74, '12px', () => {
        this.setSelectedPart(part);
      });
      this.partButtons.set(part, btn);
    });

    // Caja del área de preview
    this.place(
      this.scene.add
        .rectangle(0, 0, PREVIEW_BOX.w, PREVIEW_BOX.h, 0x000000, 0.8)
        .setOrigin(0.5)
        .setStrokeStyle(1, 0xffffff)
        .setDepth(DEPTH_BG)
        .setScrollFactor(0)
        .setVisible(false),
      PREVIEW_BOX.dx,
      PREVIEW_BOX.dy
    );

    // Container del personaje: sprites a offsets nativos, escalado entero.
    // Los pies del personaje quedan en el (0,0) local del container.
    this.previewContainer = this.place(
      this.scene.add
        .container(0, 0)
        .setDepth(DEPTH_UI)
        .setScrollFactor(0)
        .setVisible(false),
      0,
      FEET_DY
    );

    const mkSprite = (): Phaser.GameObjects.Sprite =>
      this.scene.add.sprite(0, 0, '__DEFAULT').setVisible(false);

    this.sprites = {
      body: mkSprite().setOrigin(0.5, 1),
      head: mkSprite().setOrigin(0.5, 0.5),
      helm: mkSprite().setOrigin(0.5, 0.5),
      weapon: mkSprite().setOrigin(0.5, 1),
      shield: mkSprite().setOrigin(0.5, 1),
      boots: mkSprite().setOrigin(0.5, 1),
    };
    this.previewContainer.add([
      this.sprites.shield,
      this.sprites.weapon,
      this.sprites.body,
      this.sprites.head,
      this.sprites.helm,
      this.sprites.boots,
    ]);
    this.previewContainer.setScale(this.zoom);

    // Líneas de referencia (eje del tile donde se para el personaje) + tile box.
    // Van FUERA del container para que no se engrosen con el zoom.
    this.place(
      this.scene.add
        .rectangle(0, 0, 1, PREVIEW_BOX.h, 0x00ff00, 0.7)
        .setOrigin(0.5)
        .setDepth(DEPTH_LINES)
        .setScrollFactor(0)
        .setVisible(false),
      0,
      PREVIEW_BOX.dy
    );
    this.hLine = this.place(
      this.scene.add
        .rectangle(0, 0, PREVIEW_BOX.w, 1, 0x00ff00, 0.7)
        .setOrigin(0.5)
        .setDepth(DEPTH_LINES)
        .setScrollFactor(0)
        .setVisible(false),
      0,
      FEET_DY - (TILE_SIZE / 2) * this.zoom
    );
    this.tileBox = this.place(
      this.scene.add
        .rectangle(0, 0, TILE_SIZE * this.zoom, TILE_SIZE * this.zoom, 0x000000, 0)
        .setOrigin(0.5)
        .setStrokeStyle(1, 0x00ff00, 0.5)
        .setDepth(DEPTH_LINES)
        .setScrollFactor(0)
        .setVisible(false),
      0,
      FEET_DY - (TILE_SIZE / 2) * this.zoom
    );

    // Flechas de navegación de frames
    this.makeButton('◀', -PANEL_W / 2 + 28, PREVIEW_BOX.dy, '26px', () => this.step(-1));
    this.makeButton('▶', PANEL_W / 2 - 28, PREVIEW_BOX.dy, '26px', () => this.step(1));

    // Labels de estado
    this.statusLabel = this.place(
      this.scene.add
        .text(0, 0, '', withGameFont({ fontSize: '12px', color: '#ffffff', align: 'center' }))
        .setOrigin(0.5, 0.5)
        .setDepth(DEPTH_UI)
        .setScrollFactor(0)
        .setVisible(false),
      0,
      PANEL_H / 2 - 92
    );
    this.keysLabel = this.place(
      this.scene.add
        .text(0, 0, '', withGameFont({ fontSize: '10px', color: '#999999', align: 'center' }))
        .setOrigin(0.5, 0.5)
        .setDepth(DEPTH_UI)
        .setScrollFactor(0)
        .setVisible(false),
      0,
      PANEL_H / 2 - 72
    );

    // Fila inferior: reset, play/pause, zoom
    this.makeButton('↺ reset', -160, PANEL_H / 2 - 36, '12px', () => this.resetCols());
    this.playButton = this.makeButton('▶ Play', -50, PANEL_H / 2 - 36, '12px', () =>
      this.togglePlay()
    );
    this.makeButton('−', 70, PANEL_H / 2 - 36, '14px', () => this.setZoom(this.zoom - 1));
    this.zoomLabel = this.place(
      this.scene.add
        .text(0, 0, `x${this.zoom}`, withGameFont({ fontSize: '12px', color: '#ffffaa' }))
        .setOrigin(0.5, 0.5)
        .setDepth(DEPTH_UI)
        .setScrollFactor(0)
        .setVisible(false),
      105,
      PANEL_H / 2 - 36
    );
    this.makeButton('+', 140, PANEL_H / 2 - 36, '14px', () => this.setZoom(this.zoom + 1));

    this.refreshButtonHighlights();
  }

  // ---------------------------------------------------------------------
  // Estado / interacción
  // ---------------------------------------------------------------------

  private setDirection(dir: Direction): void {
    this.direction = dir;
    // El playback manual sigue andando: colFor/walkLength ya wrappean la base
    // al largo del ciclo de la nueva dirección.
    this.refreshButtonHighlights();
    this.applyAll();
  }

  private setSelectedPart(part: PartSelection): void {
    this.selectedPart = part;
    this.refreshButtonHighlights();
    this.updateLabels();
  }

  private setZoom(zoom: number): void {
    this.zoom = Phaser.Math.Clamp(zoom, ZOOM_MIN, ZOOM_MAX);
    this.previewContainer.setScale(this.zoom);
    this.zoomLabel.setText(`x${this.zoom}`);
    // Reposicionar línea horizontal y tile box (dependen del zoom)
    const tileDy = FEET_DY - (TILE_SIZE / 2) * this.zoom;
    this.updatePlacedOffset(this.hLine, 0, tileDy);
    this.updatePlacedOffset(this.tileBox, 0, tileDy);
    this.tileBox.setSize(TILE_SIZE * this.zoom, TILE_SIZE * this.zoom);
  }

  /** Actualiza el offset registrado de un elemento y lo reposiciona. */
  private updatePlacedOffset(obj: PlacedObject, dx: number, dy: number): void {
    const entry = this.placed.find((p) => p.obj === obj);
    if (entry) {
      entry.dx = dx;
      entry.dy = dy;
    }
    obj.setPosition(this.panelX + dx, this.panelY + dy);
  }

  /** Frames del ciclo de walk según dirección (laterales: col 5 vacía). */
  private walkLength(): number {
    const lateral = this.direction === Direction.LEFT || this.direction === Direction.RIGHT;
    return lateral ? BODY_COLUMNS - 1 : BODY_COLUMNS;
  }

  /**
   * Columna efectiva de una parte: base + shift, con wrap. Durante playback el
   * wrap es sobre el largo del ciclo de walk (5 en laterales); en pausa sobre
   * las 6 columnas (para poder inspeccionar la col 5 vacía).
   */
  private colFor(part: SteppablePart): number {
    const len = this.playing ? this.walkLength() : BODY_COLUMNS;
    const v = (this.baseCol + this.shifts[part]) % len;
    return (v + len) % len;
  }

  private step(delta: number): void {
    if (this.selectedPart === 'all') {
      // Mover la columna base en bloque (pausa para inspección manual).
      if (this.playing) {
        this.stopPlayback();
      }
      this.baseCol = (((this.baseCol + delta) % BODY_COLUMNS) + BODY_COLUMNS) % BODY_COLUMNS;
    } else {
      // Shift persistente de la parte seleccionada. NO pausa: se puede
      // ajustar en vivo mientras camina, y el playback lo respeta.
      let s = (this.shifts[this.selectedPart] + delta) % BODY_COLUMNS;
      // Normalizar a un rango legible (-2..+3)
      if (s > 3) s -= BODY_COLUMNS;
      if (s < -2) s += BODY_COLUMNS;
      this.shifts[this.selectedPart] = s;
    }
    this.applyFrames();
    this.updateLabels();
  }

  private resetCols(): void {
    if (this.playing) {
      this.stopPlayback();
    }
    this.baseCol = 0;
    this.shifts = { body: 0, weapon: 0, shield: 0 };
    this.applyFrames();
    this.updateLabels();
  }

  private togglePlay(): void {
    if (this.playing) {
      this.stopPlayback();
    } else {
      this.startPlayback();
    }
  }

  /**
   * Playback manual: avanza la columna base a PLAY_FPS (sin anims de Phaser),
   * así los shifts por parte se mantienen durante la caminata.
   */
  private startPlayback(): void {
    if (!this.sprites.body.visible) return;
    this.playing = true;
    this.playAccumulatorMs = 0;
    this.playButton.setText('⏸ Pausa');
    this.applyFrames();
    this.updateLabels();
  }

  /** Pausa el playback dejando la columna base en el cuadro exacto. */
  private stopPlayback(): void {
    this.playing = false;
    this.playButton.setText('▶ Play');
    this.applyFrames();
    this.updateLabels();
  }

  // ---------------------------------------------------------------------
  // Render del preview
  // ---------------------------------------------------------------------

  /** Refresco completo: texturas + layout + orden de capas + frames + labels. */
  private applyAll(): void {
    const keys = this.callbacks.getLocalPlayerSpriteKeys();
    this.lastKeys = keys;

    const texOk = (key: string | null): boolean =>
      key !== null && key.length > 0 && this.scene.textures.exists(key);

    const setLayer = (sprite: Phaser.GameObjects.Sprite, key: string | null): boolean => {
      if (!texOk(key)) {
        sprite.setVisible(false);
        return false;
      }
      if (sprite.texture.key !== key) {
        sprite.anims.stop();
        sprite.setTexture(key as string);
      }
      sprite.setVisible(this.isOpen);
      return true;
    };

    const hadBody = setLayer(this.sprites.body, keys.bodyKey);
    setLayer(this.sprites.head, keys.headKey);
    setLayer(this.sprites.helm, keys.helmKey);
    setLayer(this.sprites.weapon, keys.weaponKey);
    setLayer(this.sprites.shield, keys.shieldKey);
    setLayer(this.sprites.boots, keys.bootKey);

    // Posiciones nativas (pies en y=0 local), mismas que GameScene.
    const bodyOffsetX = hadBody ? getBodyCenterOffset(keys.bodyId) : 0;
    const lateral = this.direction === Direction.LEFT || this.direction === Direction.RIGHT;
    const weaponDx =
      this.opts.WEAPON_OFFSET_X + (lateral ? this.opts.WEAPON_LATERAL_DX : 0);
    const headCenterY =
      -this.opts.HEAD_OFFSET_FROM_BODY_BOTTOM - this.opts.HEAD_FRAME_HEIGHT / 2;

    this.sprites.body.setPosition(bodyOffsetX, 0);
    const weaponSheetOff = getWeaponSheetOffsetForTextureKey(this.sprites.weapon.texture.key);
    this.sprites.weapon.setPosition(
      bodyOffsetX + weaponDx + weaponSheetOff.x,
      this.opts.WEAPON_OFFSET_Y + weaponSheetOff.y
    );
    this.sprites.shield.setPosition(bodyOffsetX + this.opts.SHIELD_OFFSET_X, 0);
    this.sprites.head.setPosition(0, headCenterY);
    this.sprites.helm.setPosition(0, headCenterY - 8);
    this.sprites.boots.setPosition(0, 0);

    this.applyLayerOrder();
    this.applyFrames();
    this.updateLabels();
  }

  /**
   * Orden de capas según dirección — réplica de GameScene.updateEquipmentDepth:
   * UP: arma y escudo detrás; LEFT: arma detrás, escudo delante; RIGHT: escudo
   * detrás, arma delante; DOWN: ambos delante. Head/helm/boots sobre el body.
   */
  private applyLayerOrder(): void {
    const front = 2;
    const back = -2;
    let weaponDepth = front;
    let shieldDepth = front;
    switch (this.direction) {
      case Direction.UP:
        weaponDepth = back;
        shieldDepth = back;
        break;
      case Direction.RIGHT:
        shieldDepth = back;
        break;
      case Direction.LEFT:
        weaponDepth = back;
        break;
      case Direction.DOWN:
      default:
        break;
    }
    this.sprites.body.setDepth(0);
    this.sprites.head.setDepth(1);
    this.sprites.helm.setDepth(1.1);
    this.sprites.boots.setDepth(1.5);
    this.sprites.weapon.setDepth(weaponDepth);
    this.sprites.shield.setDepth(shieldDepth);
    this.previewContainer.sort('depth');
  }

  /** Aplica los frames actuales (dirección + base + shifts) a cada capa. */
  private applyFrames(): void {
    const row = DIR_ROW[this.direction];

    // Body: frames numéricos row-major (row * 6 + col)
    if (this.sprites.body.visible) {
      this.sprites.body.setFrame(row * BODY_COLUMNS + this.colFor('body'));
    }

    // Weapon/Shield: frames `r{row}c{col}` (convención addGridSpriteSheet)
    for (const part of ['weapon', 'shield'] as const) {
      const sprite = this.sprites[part];
      if (!sprite.visible) continue;
      const frameName = gridFrameName(row, this.colFor(part));
      if (sprite.texture.has(frameName)) {
        sprite.setFrame(frameName);
      }
    }

    // Head/Helm/Boots: 1 frame por dirección
    if (this.sprites.head.visible) {
      this.sprites.head.setFrame(getHeadFrameForDirection(this.direction));
    }
    if (this.sprites.helm.visible) {
      this.sprites.helm.setFrame(getHelmetFrameForDirection(this.direction));
    }
    if (this.sprites.boots.visible) {
      this.sprites.boots.setFrame(getBootFrameForDirection(this.direction));
    }
  }

  private updateLabels(): void {
    const row = DIR_ROW[this.direction];
    const lateral = this.direction === Direction.LEFT || this.direction === Direction.RIGHT;

    const partStatus = (part: SteppablePart): string => {
      const sprite = this.sprites[part];
      if (!sprite.visible) return `${PART_LABEL[part]} —`;
      const col = this.colFor(part);
      const shift = this.shifts[part];
      const shiftStr = shift !== 0 ? `(${shift > 0 ? '+' : ''}${shift})` : '';
      let missing = false;
      if (part !== 'body') {
        missing = !sprite.texture.has(gridFrameName(row, col));
      }
      return `${PART_LABEL[part]} c${col}${shiftStr}${missing ? '!' : ''}`;
    };

    const anyCol5 = (['body', 'weapon', 'shield'] as const).some((p) => this.colFor(p) === 5);
    const emptyNote = lateral && anyCol5 ? '  (col 5 vacía en laterales)' : '';

    this.statusLabel.setText(
      `Dir ${DIR_LABEL[this.direction]} · ${partStatus('body')} · ${partStatus('weapon')} · ${partStatus('shield')}${emptyNote}`
    );

    const keys = this.lastKeys;
    if (keys) {
      const parts: string[] = [];
      if (keys.bodyKey) parts.push(keys.bodyKey);
      if (keys.headKey) parts.push(keys.headKey);
      if (keys.helmKey) parts.push(keys.helmKey);
      if (keys.weaponKey) parts.push(keys.weaponKey);
      if (keys.shieldKey) parts.push(keys.shieldKey);
      if (keys.bootKey) parts.push(keys.bootKey);
      this.keysLabel.setText(parts.length > 0 ? parts.join(' · ') : '(sin sprites)');
    }
  }

  private refreshButtonHighlights(): void {
    for (const [dir, btn] of this.dirButtons) {
      btn.setColor(dir === this.direction ? '#ffff00' : '#ffffff');
    }
    for (const [part, btn] of this.partButtons) {
      btn.setColor(part === this.selectedPart ? '#ffff00' : '#ffffff');
    }
  }

  // ---------------------------------------------------------------------
  // Loop por tick
  // ---------------------------------------------------------------------

  private onSceneUpdate(_time: number, delta: number): void {
    if (!this.isOpen) return;

    // Detectar cambios de equipamiento en vivo (equipar/desequipar con el panel
    // abierto) comparando las texture keys del render real.
    const keys = this.callbacks.getLocalPlayerSpriteKeys();
    const prev = this.lastKeys;
    const changed =
      !prev ||
      prev.bodyId !== keys.bodyId ||
      prev.bodyKey !== keys.bodyKey ||
      prev.headKey !== keys.headKey ||
      prev.weaponKey !== keys.weaponKey ||
      prev.shieldKey !== keys.shieldKey ||
      prev.helmKey !== keys.helmKey ||
      prev.bootKey !== keys.bootKey;
    if (changed) {
      this.applyAll();
    }

    // Playback manual: avanzar la columna base a PLAY_FPS. Cada parte se dibuja
    // en base + shift, así los corrimientos por parte sobreviven la caminata.
    if (this.playing) {
      this.playAccumulatorMs += delta;
      const frameMs = 1000 / PLAY_FPS;
      let advanced = false;
      while (this.playAccumulatorMs >= frameMs) {
        this.playAccumulatorMs -= frameMs;
        this.baseCol = (this.baseCol + 1) % this.walkLength();
        advanced = true;
      }
      if (advanced) {
        this.applyFrames();
        this.updateLabels();
      }
    }
  }

  // ---------------------------------------------------------------------
  // Visibilidad / movimiento
  // ---------------------------------------------------------------------

  private setVisible(visible: boolean): void {
    for (const { obj } of this.placed) {
      obj.setVisible(visible).setActive(visible);
    }
    if (visible) {
      this.applyAll();
    } else if (this.playing) {
      this.playing = false;
      this.playButton.setText('▶ Play');
    }
  }

  private move(newX: number, newY: number): void {
    this.panelX = newX;
    this.panelY = newY;
    for (const { obj, dx, dy } of this.placed) {
      obj.setPosition(newX + dx, newY + dy);
    }
  }
}
